import { Button, Drawer, Input, message as antdMessage, Modal, Select } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { sendMessage } from "../api/messages";
import { useIsMobile } from "../hooks/useBreakpoint";

export type ComposePrefill = {
  to?: string[];
  cc?: string[];
  subject?: string;
  body?: string;
  threadId?: string;
  inReplyToGmailMessageId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  prefill?: ComposePrefill;
  onSent?: () => void;
};

export function ComposeSheet({ open, onClose, prefill, onSent }: Props) {
  const isMobile = useIsMobile();
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(prefill?.to ?? []);
    setCc(prefill?.cc ?? []);
    setShowCc(Boolean(prefill?.cc?.length));
    setSubject(prefill?.subject ?? "");
    setBody(prefill?.body ?? "");
  }, [open, prefill]);

  const onSend = async () => {
    if (to.length === 0) {
      antdMessage.warning("请填写收件人");
      return;
    }
    if (!subject.trim()) {
      antdMessage.warning("请填写主题");
      return;
    }
    if (!body.trim()) {
      antdMessage.warning("请填写正文");
      return;
    }
    setSending(true);
    try {
      await sendMessage({
        to,
        ...(cc.length > 0 ? { cc } : {}),
        subject: subject.trim(),
        body,
        ...(prefill?.threadId ? { threadId: prefill.threadId } : {}),
        ...(prefill?.inReplyToGmailMessageId
          ? { inReplyToGmailMessageId: prefill.inReplyToGmailMessageId }
          : {}),
      });
      antdMessage.success("已发送");
      onSent?.();
      onClose();
    } catch {
      antdMessage.error("发送失败,请稍后重试");
    } finally {
      setSending(false);
    }
  };

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Field label="收件人">
        <Select
          mode="tags"
          value={to}
          onChange={setTo}
          placeholder="someone@example.com"
          tokenSeparators={[",", " ", ";"]}
          style={{ width: "100%" }}
        />
      </Field>
      {showCc ? (
        <Field label="抄送">
          <Select
            mode="tags"
            value={cc}
            onChange={setCc}
            placeholder="cc@example.com"
            tokenSeparators={[",", " ", ";"]}
            style={{ width: "100%" }}
          />
        </Field>
      ) : (
        <Button
          type="link"
          size="small"
          style={{ alignSelf: "flex-start", paddingLeft: 0 }}
          onClick={() => setShowCc(true)}
        >
          + 添加抄送
        </Button>
      )}
      <Field label="主题">
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="正文">
        <Input.TextArea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          autoSize={{ minRows: 8, maxRows: 18 }}
          style={{ fontSize: 16, lineHeight: 1.6 }}
        />
      </Field>
    </div>
  );

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
      <Button onClick={onClose}>取消</Button>
      <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={onSend}>
        发送
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        title="撰写邮件"
        placement="bottom"
        height="100dvh"
        open={open}
        onClose={onClose}
        maskClosable={false}
        keyboard={false}
        styles={{
          body: { paddingBottom: "calc(16px + env(safe-area-inset-bottom))" },
        }}
        footer={footer}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      title="撰写邮件"
      open={open}
      onCancel={onClose}
      width={680}
      footer={footer}
      centered
      destroyOnHidden
      maskClosable={false}
      keyboard={false}
    >
      {content}
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 14,
          color: "var(--ant-color-text-secondary)",
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
